import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMedia } from "../../media/MediaContext";

import Api from "../../../shared/api/api";
import BlogPreview from "../components/blog/BlogPreview";
import YouTubeSection from "../components/blog/YouTubeSection";
import "../styles/BlogPage.css";

export default function BlogPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { config, loading: settingsLoading } = useMedia();
  const tabs = [{ id: "blog", label: "Blog" }, ...(config?.blog.videosEnabled ? [{ id: "videos", label: "Videos & Shorts" }] : [])];
  const requestedView = params.get("view") || config?.blog.defaultTab || "blog";
  const requestedTab = requestedView === "shorts" ? "videos" : requestedView;
  const activeTab = tabs.some(tab => tab.id === requestedTab) ? requestedTab : "blog";
  function selectTab(id) {
    setParams(previous => { const next = new URLSearchParams(previous); next.set("view", id); return next; });
  }

  const [blogs, setBlogs] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let isMounted = true;

    async function fetchBlogs() {
      try {
        setStatus("loading");

        const response = await Api.getAllAuthor("blog");

        if (!isMounted) return;

        const blogRecords = Array.isArray(response)
          ? response
          : response?.blogs || response?.data || [];

        setBlogs(Array.isArray(blogRecords) ? blogRecords : []);
        setStatus("success");
      } catch (error) {
        if (!isMounted) return;

        console.error("Error loading blogs:", error);
        setStatus("error");
      }
    }

    fetchBlogs();

    return () => {
      isMounted = false;
    };
  }, []);

  const sortedBlogs = useMemo(() => {
    return [...blogs].sort((firstBlog, secondBlog) => {
      const firstDate = new Date(firstBlog?.date || 0).getTime();
      const secondDate = new Date(secondBlog?.date || 0).getTime();

      return secondDate - firstDate;
    });
  }, [blogs]);

  function handleBlogClick(blog) {
    if (!blog?.title) return;

    navigate(`/blog/${encodeURIComponent(blog.title)}`);
  }

  return (
    <main className="blogPageContainer">
      <h1>Blog & videos</h1>
      {settingsLoading ? <p role="status">Loading page preferences…</p> : <>
      <div className="blogViewTabs" role="tablist" aria-label="Blog content">
        {tabs.map((tab, index) => <button key={tab.id} type="button" role="tab" id={`blog-tab-${tab.id}`} aria-controls="blog-content-panel" aria-selected={activeTab === tab.id} tabIndex={activeTab === tab.id ? 0 : -1} onClick={() => selectTab(tab.id)} onKeyDown={event => {
          let nextIndex;
          if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
          if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === "Home") nextIndex = 0;
          if (event.key === "End") nextIndex = tabs.length - 1;
          if (nextIndex !== undefined) { event.preventDefault(); selectTab(tabs[nextIndex].id); document.getElementById(`blog-tab-${tabs[nextIndex].id}`)?.focus(); }
        }}>{tab.label}</button>)}
      </div>
      <div role="tabpanel" id="blog-content-panel" aria-labelledby={`blog-tab-${activeTab}`} tabIndex={0}>
      {activeTab === "blog" ? <>

      {status === "loading" && (
        <p className="loadingMsg" role="status">
          Loading blog posts...
        </p>
      )}

      {status === "error" && (
        <p className="warningError" role="alert">
          Blog posts could not be loaded. Please try again later.
        </p>
      )}

      {status === "success" && sortedBlogs.length === 0 && (
        <p className="blogEmptyMessage">
          No blog posts are currently available.
        </p>
      )}

      {status === "success" && sortedBlogs.length > 0 && (
        <section className="blogsGrid" aria-label="Blog posts">
          {sortedBlogs.map((blog) => {
            const blogKey =
              blog.id ||
              blog._id ||
              `${blog.title || "untitled"}-${blog.date || "undated"}`;

            return (
              <BlogPreview
                key={blogKey}
                blog={blog}
                onClick={() => handleBlogClick(blog)}
              />
            );
          })}
        </section>
      )}
      </> : <YouTubeSection title="Author Videos & Shorts" />}
      </div>
      </>}
    </main>
  );
}
